import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories, siteInfo } from '@/mocks/site';

const navLinks = [
  { label: '首页', to: '/' },
  { label: '资源库', to: '/library' },
  { label: '关于我们', to: '/about' },
];

const DARK_TOP_PREFIX = ['/topic/'];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  const path = location.pathname;
  const isDarkTop = path === '/' || path === '/about' || DARK_TOP_PREFIX.some((p) => path.startsWith(p));
  const transparent = !scrolled && isDarkTop;

  const linkBase = 'px-4 py-2 rounded-md text-sm transition-colors';
  const linkIdle = transparent
    ? 'text-background-100 hover:bg-background-50/10 hover:text-background-50'
    : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100';
  const linkActive = transparent ? 'text-accent-400 bg-background-50/10' : 'text-primary-600 bg-primary-50 font-medium';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        transparent ? 'bg-transparent border-transparent' : 'bg-background-50/95 backdrop-blur border-background-200'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-newspaper-line text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading text-xl font-bold tracking-wide ${
                  transparent ? 'text-background-50' : 'text-foreground-950'
                }`}
              >
                {siteInfo.name}
              </span>
              <span
                className={`text-[11px] hidden sm:block mt-0.5 ${
                  transparent ? 'text-background-100/80' : 'text-foreground-500'
                }`}
              >
                热点专题资源库
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
            {navLinks.map((link) => {
              const active = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`${linkBase} ${active ? linkActive : linkIdle}`}
                >
                  {link.label}
                </Link>
              );
            })}
            <div className="relative">
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className={`${linkBase} flex items-center gap-1 cursor-pointer whitespace-nowrap ${linkIdle}`}
                aria-expanded={catOpen}
              >
                热门分类
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="absolute right-0 mt-2 w-64 p-2 rounded-lg border border-background-200 bg-background-50 shadow-lg">
                  {categories.map((c) => (
                    <Link
                      key={c.id}
                      to={`/library?cat=${c.id}`}
                      className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-background-100 transition-colors"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                        <i className="ri-price-tag-3-line" />
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm text-foreground-900">{c.name}</span>
                        <span className="text-xs text-foreground-500">{c.count} 个专题</span>
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/library"
              className="px-5 py-2.5 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              逛专题
            </Link>
          </div>

          <button
            type="button"
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              transparent ? 'text-background-50' : 'text-foreground-900'
            }`}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="切换菜单"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-3 py-3 rounded-md text-base text-foreground-900 hover:bg-background-100 transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="mt-3 pt-3 border-t border-background-200">
            <p className="px-3 text-xs text-foreground-500 mb-2">热门分类</p>
            <div className="flex flex-wrap gap-2 px-3">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/library?cat=${c.id}`}
                  className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-900 whitespace-nowrap"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}